/**
 * Tree queries + Slice-4 branching helpers for the FK Breadcrumb. Split out
 * of `breadcrumbTree.ts` to keep both files under the 200-line CLAUDE.md cap.
 * Re-exported from `breadcrumbTree` so callers can keep importing everything
 * from one place.
 *
 * `stackChainFromRoot` / `stackChainLength` are kept for legacy callers
 * (serialization tests + Slice-3 specs that pre-date branching). The
 * chip-row renders via `activePathFromRoot` (`breadcrumbTree.ts`) +
 * `computeTreeLayout` (`breadcrumbTreeLayout.ts`) which fans out off-path
 * siblings into their own rows.
 */

import { activate, collectSubtree, dismiss, push } from './breadcrumbTree';
import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
} from '../../../core/types/workspaceTypes';

/** Slice 6 — keyboard nav directions (`⌘← ⌘→ ⌘↑ ⌘↓`). */
export type BreadcrumbKey = 'left' | 'right' | 'up' | 'down';

/**
 * Stack-shape chain from root through `childIds[0]` until a leaf. Kept for
 * legacy callers — Slice 4 renders the active path, not the full chain.
 */
export function stackChainFromRoot(tree: NavigationTree): NavigationNode[] {
  const out: NavigationNode[] = [];
  let cur: NavigationNode | undefined = tree.nodes[tree.rootId];
  while (cur) {
    out.push(cur);
    const nextId: string | undefined = cur.childIds[0];
    cur = nextId ? tree.nodes[nextId] : undefined;
  }
  return out;
}

/** Total node count along the stack chain (legacy). */
export function stackChainLength(tree: NavigationTree): number {
  return stackChainFromRoot(tree).length;
}

/**
 * Push `child` as a new child of the active node and activate it. Preserves
 * the active node's existing children (siblings of the new child). This is
 * the same behavior as `push` — re-exported under a Slice-4 name to make
 * the intent at call-sites explicit.
 */
export function pushChild(
  tree: NavigationTree,
  child: NavigationNode,
): { tree: NavigationTree; newId: NavigationNodeId } {
  return push(tree, child);
}

/** Return all immediate children of `id` in declaration order. */
export function getChildren(tree: NavigationTree, id: NavigationNodeId): NavigationNode[] {
  const node = tree.nodes[id];
  if (!node) return [];
  return node.childIds
    .map((cid) => tree.nodes[cid])
    .filter((n): n is NavigationNode => Boolean(n));
}

/**
 * Return all SIBLINGS of `id` (children of `id`'s parent, excluding `id`).
 * Returns an empty array when `id` is the root or unknown.
 */
export function getSiblings(tree: NavigationTree, id: NavigationNodeId): NavigationNode[] {
  const node = tree.nodes[id];
  if (!node || node.parentId === null) return [];
  const parent = tree.nodes[node.parentId];
  if (!parent) return [];
  return parent.childIds
    .filter((cid) => cid !== id)
    .map((cid) => tree.nodes[cid])
    .filter((n): n is NavigationNode => Boolean(n));
}

/**
 * Set the active node without mutating tree shape. Alias of `activate` named
 * for Slice-4 call-sites (chip click on any row of the multi-row tree).
 */
export function setActive(tree: NavigationTree, id: NavigationNodeId): NavigationTree {
  return activate(tree, id);
}

/**
 * Remove `id` and its entire subtree from the tree. Same semantics as
 * `dismiss` — re-exported under a Slice-4 name to make pruning explicit at
 * the call-site (e.g. "user picked a different branch, prune the old one").
 *
 * Returns `null` when the pruned node is the root (i.e. the whole tree is
 * cleared); callers should treat that as "clear the tab's navigationTree".
 */
export function pruneSubtree(
  tree: NavigationTree,
  id: NavigationNodeId,
): NavigationTree | null {
  return dismiss(tree, id);
}

/** Convenience: union of subtree ids for an arbitrary node id. */
export function subtreeIdsOf(tree: NavigationTree, id: NavigationNodeId): ReadonlyArray<NavigationNodeId> {
  return collectSubtree(tree, id);
}

/** Parent of `id`, or null if root / missing. */
export function parentOf(tree: NavigationTree, id: NavigationNodeId): NavigationNode | null {
  const node = tree.nodes[id];
  if (!node || node.parentId === null) return null;
  return tree.nodes[node.parentId] ?? null;
}

/** First child of `id`, or null. */
export function firstChildOf(tree: NavigationTree, id: NavigationNodeId): NavigationNode | null {
  const node = tree.nodes[id];
  if (!node) return null;
  const firstId = node.childIds[0];
  return firstId ? (tree.nodes[firstId] ?? null) : null;
}

/**
 * Resolve the next-active node for a keyboard shortcut against the active node.
 *
 *  - `up`    — activate parent (or stay if root).
 *  - `down`  — activate first child (or stay if leaf).
 *  - `left`  — previous sibling (or previous on active-path if no siblings).
 *  - `right` — next sibling (or next on active-path if no siblings).
 *
 * Returns the target node id, or `null` when the shortcut has no effect.
 */
export function resolveKeyboardTarget(
  tree: NavigationTree,
  key: BreadcrumbKey,
): NavigationNodeId | null {
  const active = tree.nodes[tree.activeNodeId];
  if (!active) return null;

  if (key === 'up') {
    return active.parentId ?? null;
  }
  if (key === 'down') {
    const first = active.childIds[0];
    return first ?? null;
  }

  // Sibling moves: look up siblings via parent.childIds (full list, not the
  // `getSiblings` helper which strips `id`). If active is the root or has no
  // siblings, fall back to the active path so the user can still walk left/right
  // through the breadcrumb.
  const parent = active.parentId ? tree.nodes[active.parentId] : null;
  const siblings = parent?.childIds ?? [];
  const idxInSiblings = siblings.indexOf(active.id);
  if (siblings.length > 1 && idxInSiblings >= 0) {
    const nextIdx = key === 'right' ? idxInSiblings + 1 : idxInSiblings - 1;
    if (nextIdx >= 0 && nextIdx < siblings.length) return siblings[nextIdx];
    return null;
  }

  // Fallback for stack-shape trees: walk the chain.
  //   - `left` goes UP to the parent (handled here, not via active-path
  //     because `activePathFromRoot` ends AT the active node, so an active
  //     middle-node has no "next" path entry).
  //   - `right` goes DOWN via `childIds[0]` (the canonical stack chain).
  if (key === 'left') {
    return active.parentId ?? null;
  }
  const downId = active.childIds[0];
  return downId ?? null;
}
